import { getClientTarget } from "@repo/client";
import { type Logger, Named } from "@repo/logger";
import type { useActor } from "@xstate/solid";
import {
  type SendToServerEvent,
  type WebSocketStore,
  type WebsocketMachineStates,
  type WsEvents,
  newConnectedEvent,
  newDisconnectedEvent,
  newReceiveFromServerEvent,
} from "@core/domains/ws/machines";

export type SSEServiceDependencies = {
  actor: ReturnType<typeof useActor>;
  logger: Logger;
  getAuthToken: () => string | undefined;
  isIdentityConnecting: () => boolean;
};

export interface SSEService {
  snapshot: {
    value: WebsocketMachineStates;
    context: WebSocketStore;
  };
  send: ReturnType<typeof useActor>[1];
  actor: ReturnType<typeof useActor>[2];
  connect: () => void;
  disconnect: () => void;
}

export const useSSEService = (deps: SSEServiceDependencies): SSEService => {
  const [snapshot, send, actor] = deps.actor;
  const logger = new Named(deps.logger, "sseService");
  actor.start();

  // Track active fetch requests
  const activeRequests = new Map<string, AbortController>();
  let connected = false;

  // Simple method to indicate connection status
  const connect = () => {
    if (!connected) {
      connected = true;
      send(newConnectedEvent());
    }
  };

  const disconnect = () => {
    // Abort all active requests
    activeRequests.forEach((controller, id) => {
      controller.abort();
      activeRequests.delete(id);
    });

    connected = false;
    send(newDisconnectedEvent());
  };

  // Process a command by sending it to the server and processing the stream
  const processCommand = async (envelope: SendToServerEvent["envelope"]) => {
    const token = deps.getAuthToken();
    if (!token) {
      logger.warn("No token available for sending command");
      return;
    }

    // Generate a unique ID for this command
    const commandId = Date.now().toString(36);

    // Create an AbortController for this request
    const abortController = new AbortController();
    activeRequests.set(commandId, abortController);

    try {
      const target = getClientTarget();
      logger.info("Sending command to stream endpoint", { commandId, envelope });

      const response = await fetch(`${target}/v1/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(envelope),
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
      }

      // Get a reader from the response body
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("No reader available");
      }

      connect(); // Mark as connected once we establish a stream

      // Process the stream data
      let buffer = "";
      const decoder = new TextDecoder();
      let isFinished = false;

      let lastProcessedTime = 0;

      // Only process new stream data every 150ms to avoid overwhelming the renderer
      const BUFFER_TIME_THRESHOLD = 150;

      const readLoop = async ({ forceProcessing = false }: { forceProcessing?: boolean } = {}) => {
        const { done, value } = await reader.read();

        if (done) {
          logger.info("Stream finished", { commandId });
          isFinished = true;
          return;
        }

        // Decode the chunk and add to buffer
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        if (!forceProcessing && performance.now() - lastProcessedTime < BUFFER_TIME_THRESHOLD) {
          return;
        }

        lastProcessedTime = performance.now();

        // Process the buffer to extract complete JSON objects
        let processedPos = 0;
        let bracketCount = 0;
        let jsonStartPos = -1;

        // Scan through the buffer character by character
        for (let pos = 0; pos < buffer.length; pos++) {
          const char = buffer[pos];

          // Track opening brackets to find JSON object boundaries
          if (char === "{") {
            if (bracketCount === 0) {
              jsonStartPos = pos;
            }
            bracketCount++;
          } else if (char === "}") {
            bracketCount--;

            // When bracket count returns to 0, we have a complete JSON object
            if (bracketCount === 0 && jsonStartPos !== -1) {
              const jsonStr = buffer.substring(jsonStartPos, pos + 1);

              try {
                const data = JSON.parse(jsonStr);
                logger.info("Received data from stream", { commandId });
                send(newReceiveFromServerEvent(data));
              } catch (error) {
                logger.error("JSON_PARSE_ERROR", "Failed to parse JSON from stream", { error, jsonStr });
              }

              // Update the processed position
              processedPos = pos + 1;
            }
          }
        }

        // Remove processed JSON objects from buffer, keeping any incomplete JSON
        if (processedPos > 0) {
          buffer = buffer.substring(processedPos);
        }
      };

      while (!isFinished) {
        await readLoop({ forceProcessing: false });
      }

      // Last one as cleanup;
      await readLoop({ forceProcessing: true });
    } catch (error) {
      if (error instanceof Error) {
        if (error.name === "AbortError") {
          logger.info("Request was aborted", { commandId });
        } else {
          logger.error(error.name, "Error processing command stream", { error, commandId });
          send(newDisconnectedEvent());
        }
      } else {
        logger.error("UNKNOWN_ERROR", "Error processing command stream", { error, commandId });
        send(newDisconnectedEvent());
      }
    } finally {
      activeRequests.delete(commandId);
    }
  };

  // Helper function to find the end of a JSON object in a string
  const findJsonObjectEnd = (str: string, startPos: number): number => {
    let pos = startPos;
    while (pos < str.length && /\s/.test(str[pos] || "")) pos++; // Skip whitespace

    if (pos >= str.length || str[pos] !== "{") return -1; // Must start with {

    let depth = 1;
    pos++; // Move past the first {

    while (pos < str.length && depth > 0) {
      if (str[pos] === "{") depth++;
      else if (str[pos] === "}") depth--;
      pos++;
    }

    return depth === 0 ? pos - 1 : -1;
  };

  return {
    snapshot,
    send: (event: WsEvents) => {
      // If this is a send event, process the command
      if (event.type === "ws.send" && event.envelope) {
        processCommand(event.envelope);
      }

      // Forward the event to the state machine
      send(event);
    },
    actor,
    connect,
    disconnect,
  };
};
